import * as React from 'react';
import { useMemo } from 'react';

import { useQuery } from '@apollo/client';

import type {
  ClipsQueryQuery as ClipsQueryData,
  ClipsQueryQueryVariables,
} from 'bundles/enterprise-admin-program-catalog/api/__generated__/clipsQuery';
import ClipsQuery from 'bundles/enterprise-admin-program-catalog/api/clipsQuery.graphql';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import { useCuratedCollections } from 'bundles/page-config-common/contexts/enterprise/EnterpriseCuratedCollectionsContext';
import EnterpriseCurriculumClipsContext from 'bundles/page-config-common/contexts/enterprise/EnterpriseCurriculumClipsContext';
import { useForCreditCollection } from 'bundles/page-config-common/contexts/enterprise/EnterpriseForCreditCollectionContext';

const EnterpriseCurriculumClipsProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const { forCreditCollection, forCreditCollectionLoading } = useForCreditCollection();
  const { curatedCollections, curatedCollectionsLoading } = useCuratedCollections();

  const forCreditItems = forCreditCollection?.items;
  const curatedCollectionsItems = curatedCollections?.map((curatedCollection) => curatedCollection?.items) || [];

  const allCollectionItems = [forCreditItems, ...curatedCollectionsItems].filter(Boolean).flat();

  const clipItems = useMemo(
    () =>
      allCollectionItems
        .filter((item) => !!item?.clipId)
        .map((item) => ({ itemId: item?.clipId?.itemId, courseId: item?.clipId?.courseId })),
    [allCollectionItems]
  ) as Array<{
    itemId: string;
    courseId: string;
  }>;

  const { data, loading } = useQuery<ClipsQueryData, ClipsQueryQueryVariables>(ClipsQuery, {
    skip: forCreditCollectionLoading || curatedCollectionsLoading || clipItems.length === 0,
    variables: {
      ids: clipItems,
    },
    context: {
      clientName: 'gatewayGql',
    },
    ssr: false,
    errorPolicy: 'all' as const,
  });

  if (forCreditCollectionLoading || curatedCollectionsLoading || loading) {
    return (
      <EnterpriseCurriculumClipsContext.Provider value={{ clipsLoading: true, clips: undefined }}>
        {children}
      </EnterpriseCurriculumClipsContext.Provider>
    );
  }

  const clips = filterExistsOrDefault(data?.Clip?.queryClipItemsByIds);

  return (
    // @ts-expect-error URL: string
    <EnterpriseCurriculumClipsContext.Provider value={{ clipsLoading: false, clips }}>
      {children}
    </EnterpriseCurriculumClipsContext.Provider>
  );
};

export default EnterpriseCurriculumClipsProvider;
